'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { apiPost, AssembledServiceResponse, LiturgicalDay, useApi } from '@/lib/api';
import { useTopbarTitle } from '@/lib/topbar';
import { AdminDatePicker, type DatePickerLocale } from '@/components/AdminDatePicker';

/* ─── Font Scale ─── */
const FONT_SCALES = [
  { labelKey: 'small', value: 0.85 },
  { labelKey: 'normal', value: 1.0 },
  { labelKey: 'large', value: 1.25 },
  { labelKey: 'very_large', value: 1.5 },
  { labelKey: 'maximum', value: 1.85 },
];
const DEFAULT_SCALE = 1.5;
const FONT_SCALE_KEY = 'typikon-font-scale';

function useFontScale() {
  const [scale, setScale] = useState(DEFAULT_SCALE);

  useEffect(() => {
    const saved = localStorage.getItem(FONT_SCALE_KEY);
    if (saved) setScale(parseFloat(saved));
  }, []);

  useEffect(() => {
    localStorage.setItem(FONT_SCALE_KEY, String(scale));
    document.documentElement.style.setProperty('--reading-scale', String(scale));
  }, [scale]);

  return [scale, setScale] as const;
}

/* ─── Service Types — matches backend ServiceType enum ─── */
const SERVICE_TYPES = [
  'vespers', 'matins', 'vigil', 'hours', 'royal_hours', 'paschal_hours',
  'liturgy', 'presanctified', 'tipika', 'compline', 'midnight_office',
] as const;

/* ─── Time-based service auto-detection (Typikon daily cycle) ─── */
const TIME_SERVICE_MAP: { hourStart: number; hourEnd: number; serviceType: string; subType?: string }[] = [
  { hourStart: 0,  hourEnd: 1,  serviceType: 'midnight_office', subType: 'daily' },
  { hourStart: 2,  hourEnd: 6,  serviceType: 'matins',          subType: 'daily' },
  { hourStart: 7,  hourEnd: 8,  serviceType: 'hours',           subType: 'first' },
  { hourStart: 9,  hourEnd: 9,  serviceType: 'hours',           subType: 'third' },
  { hourStart: 10, hourEnd: 11, serviceType: 'liturgy',         subType: 'chrysostom' },
  { hourStart: 12, hourEnd: 12, serviceType: 'hours',           subType: 'sixth' },
  { hourStart: 13, hourEnd: 14, serviceType: 'tipika' },
  { hourStart: 15, hourEnd: 17, serviceType: 'hours',           subType: 'ninth' },
  { hourStart: 18, hourEnd: 19, serviceType: 'vespers',         subType: 'daily' },
  { hourStart: 20, hourEnd: 23, serviceType: 'compline',        subType: 'small' },
];

/* ─── Fallback chain: when auto-detected service has no template ─── */
const SERVICE_FALLBACK_MAP: Record<string, { serviceType: string; subType?: string }> = {
  midnight_office: { serviceType: 'matins', subType: 'daily' },
  compline: { serviceType: 'vespers', subType: 'daily' },
  tipika: { serviceType: 'liturgy', subType: 'chrysostom' },
  presanctified: { serviceType: 'liturgy', subType: 'chrysostom' },
  vigil: { serviceType: 'vespers', subType: 'daily' },
  royal_hours: { serviceType: 'hours', subType: 'first' },
  paschal_hours: { serviceType: 'hours', subType: 'first' },
};

function getServiceByTime(date: Date): { serviceType: string; subType?: string } {
  const hour = date.getHours();
  for (const zone of TIME_SERVICE_MAP) {
    if (hour >= zone.hourStart && hour <= zone.hourEnd) {
      return { serviceType: zone.serviceType, subType: zone.subType };
    }
  }
  return { serviceType: 'liturgy', subType: 'chrysostom' };
}

/* ─── SVG Icons ─── */
function CalendarIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function BookIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
    </svg>
  );
}

function ListIcon({ size = 20 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>;
}

/* ─── SeasonFlags display ─── */
function SeasonFlagsBar({ flags, t }: { flags: Record<string, unknown>; t: (key: string) => string }) {
  if (!flags || !flags.season) return null;

  const items: { label: string; accent?: boolean }[] = [];

  // Season
  const seasonLabels: Record<string, string> = {
    ordinary: t('service.season_ordinary'),
    preparatory: t('service.season_preparatory'),
    great_lent: t('service.season_great_lent'),
    holy_week: t('service.season_holy_week'),
    bright_week: t('service.season_bright_week'),
    pentecostarion: t('service.season_pentecostarion'),
  };
  const season = flags.season as string;
  if (seasonLabels[season]) {
    items.push({ label: seasonLabels[season], accent: season !== 'ordinary' });
  }

  // Tone
  if (flags.tone) {
    items.push({ label: `${t('service.tone_label')} ${flags.tone}` });
  }

  // Key flags
  if (flags.efrem_prayer) items.push({ label: t('service.flag_efrem'), accent: true });
  if (flags.christos_voskrese) items.push({ label: t('service.flag_christos_voskrese'), accent: true });
  if (!flags.liturgy_available) items.push({ label: t('service.flag_no_liturgy'), accent: true });
  if (flags.is_hierarchical) items.push({ label: t('service.flag_hierarchical'), accent: true });
  if (flags.is_cross_week) items.push({ label: t('service.flag_cross'), accent: true });

  // Resolution fields
  if (flags.antiphon_type && flags.antiphon_type !== 'daily') {
    items.push({ label: t(`service.antiphon_${flags.antiphon_type}`) });
  }
  if (flags.trisagion_variant && flags.trisagion_variant !== 'standard') {
    items.push({ label: t(`service.trisagion_${flags.trisagion_variant}`) });
  }
  if (flags.cherubic_variant && flags.cherubic_variant !== 'standard') {
    items.push({ label: t(`service.cherubic_${flags.cherubic_variant}`) });
  }
  if (flags.megalynarion_type && flags.megalynarion_type !== 'dostoino') {
    items.push({ label: t(`service.megalynarion_${flags.megalynarion_type}`) });
  }

  if (items.length === 0) return null;

  return (
    <div className="season-flags">
      {items.map((item, i) => (
        <span key={i} className={`season-flag${item.accent ? ' season-flag--accent' : ''}`}>
          {item.label}
        </span>
      ))}
    </div>
  );
}

/* ─── Shared Content Renderer ─── */
function ServiceContent({ assembled, dayInfo, error, serviceType, fallbackUsed, t, locale }: {
  assembled: AssembledServiceResponse | null;
  dayInfo: any;
  error: string | null;
  serviceType: string;
  fallbackUsed: string | null;
  t: any;
  locale: string;
}) {
  const slotLabels = t.raw('slot_labels') as Record<string, string>;

  if (error) {
    return <div className="content-error">{error}</div>;
  }

  /* ─── Fallback notification banner ─── */
  const fallbackBanner = fallbackUsed ? (() => {
    const serviceNames = t.raw('service_types') as Record<string, string>;
    const originalName = serviceNames[serviceType] || serviceType;
    const fallbackName = serviceNames[fallbackUsed] || fallbackUsed;
    return (
      <div className="fallback-banner">
        {t('service.fallback_notification', { service: originalName, fallback: fallbackName })}
      </div>
    );
  })() : null;

  if (!assembled) {
    return (
      <div className="content-empty">
        <div className="divider">✦ ✦ ✦</div>
        <h1>{t('home.title')}</h1>
        <p>{t('home.description')}</p>
      </div>
    );
  }

  const blocks = assembled.blocks ?? [];
  const lections = assembled.lections ?? {};
  const patronTroparia = assembled.patron_troparia ?? { has_patron: false };
  const seasonFlags = (assembled.season_flags ?? {}) as Record<string, unknown>;

  return (
    <>
      {/* Fallback notification */}
      {fallbackBanner}
      {/* Title Block */}
      <div className="title-block">
        <h1>
          {(t.raw('service_types') as Record<string, string>)[serviceType] || serviceType}
        </h1>
        {dayInfo && (
          <div className="subtitle">
            {(t.raw('days_of_week') as Record<string, string>)[dayInfo.day_of_week_name] || dayInfo.day_of_week_name}
          </div>
        )}
        <div className="meta">
          {dayInfo && (
            <>
              {dayInfo.gregorian_date}
              {dayInfo.tone && ` · ${t('service.tone_label')} ${dayInfo.tone}`}
              {dayInfo.fasting && ` · ${(t.raw('fasting_types') as Record<string, string>)[dayInfo.fasting] || dayInfo.fasting}`}
            </>
          )}
        </div>
      </div>

      {/* SeasonFlags */}
      <SeasonFlagsBar flags={seasonFlags} t={t} />

      <div className="divider">✦ ✦ ✦</div>

      {/* Patron Troparia */}
      {patronTroparia.has_patron && (
        <div id="section-patron" data-nav-title={t('service.patron_troparia')} className="section">
          <span className="block-marker">TROPARIA</span>
          <h2 className="section-title">{t('service.patron_troparia')}</h2>
          <div className="rubric">
            {patronTroparia.saint_name}
            {patronTroparia.dedication_type && ` (${patronTroparia.dedication_type})`}
          </div>
          {patronTroparia.troparion && (
            <div className="lit-text">
              <p className="red-init">
                {t('service.troparion_tone')} {patronTroparia.troparion.tone}.{' '}
                {patronTroparia.troparion.text}
              </p>
            </div>
          )}
          {patronTroparia.kontakion && (
            <div className="lit-text">
              <p>
                {t('service.kontakion_tone')} {patronTroparia.kontakion.tone}.{' '}
                {patronTroparia.kontakion.text}
              </p>
            </div>
          )}
          <div className="divider-line"><span>✦</span></div>
        </div>
      )}

      {/* Service Blocks — continuous flow */}
      {blocks.map((block, i) => (
        <div
          key={`${block.slot_key}-${i}`}
          id={`section-${i}`}
          data-nav-title={slotLabels[block.slot_key] || block.title || block.slot_key}
          className="section"
        >
          <span className="block-marker">{block.block_type.toUpperCase()}</span>
          {(block.title || slotLabels[block.slot_key]) && <h2 className="section-title">{block.title || slotLabels[block.slot_key]}</h2>}
          {block.rubric && <div className="rubric">{block.rubric}</div>}
          {block.content && (
            <div className="lit-text">
              {block.content.includes('\n') ? (
                block.content.split('\n').filter((line: string) => line.trim()).map((line: string, li: number) => (
                  <p key={li} className="red-init">{line}</p>
                ))
              ) : (
                <p className="red-init">{block.content}</p>
              )}
            </div>
          )}
          {block.content_translated && (
            <div className="lit-text content-translated">
              {block.content_translated.includes('\n') ? (
                block.content_translated.split('\n').filter((line: string) => line.trim()).map((line: string, li: number) => (
                  <p key={li}>{line}</p>
                ))
              ) : (
                <p>{block.content_translated}</p>
              )}
            </div>
          )}
          <div className="divider-line"><span>✦</span></div>
        </div>
      ))}

      {/* Lections — continuous flow */}
      {Object.keys(lections).length > 0 && (
        <>
          {Object.entries(lections).map(([book, lections]) => (
            <div key={book}>
              {lections.map((lec, li) => {
                const sectionId = `lection-${book}-${li}`;
                return (
                  <div
                    key={sectionId}
                    id={sectionId}
                    data-nav-title={lec.short_ref}
                    className="section"
                  >
                    <span className="block-marker">LECTION</span>
                    <div className="lection">
                      <div className="lection-ref">
                        {(t.raw('books') as Record<string, string>)[book] || book}
                        {' · '}
                        {lec.short_ref}
                        <span className="zachalo">Zachalo {lec.zachalo}</span>
                        {lec.is_paremia && <span className="paremia-label">Paremia</span>}
                      </div>
                      <div className="lit-text">
                        {lec.content ? (
                          <p className={li === 0 ? 'red-init' : ''}>
                            <strong className="incipit">{lec.title}</strong>{' '}
                            {lec.content}
                          </p>
                        ) : (
                          <p><strong className="incipit">{lec.title}</strong></p>
                        )}
                      </div>
                    </div>
                    <div className="divider-line"><span>✦</span></div>
                  </div>
                );
              })}
            </div>
          ))}
        </>
      )}
    </>
  );
}

/* ─── Main Page ─── */
export default function ServicePage() {
  const t = useTranslations();
  const locale = useLocale();
  const { setTitle: setTopbarTitle } = useTopbarTitle();
  const [date, setDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [serviceType, setServiceType] = useState(() => getServiceByTime(new Date()).serviceType);
  const [subType, setSubType] = useState<string | undefined>(() => getServiceByTime(new Date()).subType);
  const [calendarStyle, setCalendarStyle] = useState<'new' | 'old'>('new');
  const [mode, setMode] = useState<'full' | 'ustav'>('full');
  const [isHierarchical, setIsHierarchical] = useState(false);
  const [templeId] = useState(1);
  const [assembled, setAssembled] = useState<AssembledServiceResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fallbackUsed, setFallbackUsed] = useState<string | null>(null);

  /* Update topbar breadcrumb with service name */
  useEffect(() => {
    if (assembled) {
      const serviceName = (t.raw('service_types') as Record<string, string>)[serviceType] || serviceType;
      setTopbarTitle(serviceName);
    } else {
      setTopbarTitle('');
    }
  }, [assembled, serviceType, t, setTopbarTitle]);

  const [fontScale, setFontScale] = useFontScale();
  const [showFontMenu, setShowFontMenu] = useState(false);
  const [showServicePicker, setShowServicePicker] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [showRunningHeader, setShowRunningHeader] = useState(false);

  // Mobile
  const [showTocSheet, setShowTocSheet] = useState(false);
  const [showServiceSheet, setShowServiceSheet] = useState(false);
  const [showMobileSettings, setShowMobileSettings] = useState(false);

  const contentRef = useRef<HTMLDivElement>(null);

  /* Calendar locale config from i18n */
  const calLocale: DatePickerLocale = {
    months: t.raw('months') as string[],
    monthsShort: t.raw('months_short') as string[],
    weekdays: t.raw('weekdays_short') as string[],
    todayLabel: t('service.cal_today'),
    clearLabel: t('service.cal_clear'),
    modalTitleLabel: (t.raw('service') as Record<string, unknown>).cal_modal_title as string | undefined,
    confirmLabel: (t.raw('service') as Record<string, unknown>).cal_confirm as string | undefined,
    cancelLabel: (t.raw('app') as Record<string, unknown>)?.cancel as string | undefined,
    formatDate: (y: number, m: number, d: number) =>
      `${d} ${(t.raw('months') as string[])[m]?.toLowerCase() ?? ''} ${y}`,
  };

  const { data: litDay } = useApi<LiturgicalDay>(
    `/calendar/date/${date}?style=${calendarStyle}`
  );

  const assemble = useCallback(async () => {
    setLoading(true);
    setError(null);
    setFallbackUsed(null);
    const lang = locale === 'ru' ? 'ru' : 'fr';
    const hierParam = isHierarchical ? '&is_hierarchical=true' : '';

    // Try current service type first
    const subParam = subType ? `&sub_type=${subType}` : '';
    try {
      const result = await apiPost<AssembledServiceResponse>(
        `/service/assemble?target_date=${date}&service_type=${serviceType}&temple_id=${templeId}&language=${lang}&calendar_style=${calendarStyle}&mode=${mode}${hierParam}${subParam}`,
        {}
      );
      setAssembled(result);
      return;
    } catch (err: any) {
      const errMsg = err.message || '';
      const isTemplateMissing = errMsg.includes('No') && errMsg.includes('template found');

      if (!isTemplateMissing) {
        // Real error (not missing template) — show it
        setError(errMsg || t('app.error'));
        setAssembled(null);
        setLoading(false);
        return;
      }

      // Template missing — try fallback chain
      const fallback = SERVICE_FALLBACK_MAP[serviceType];
      if (!fallback) {
        setError(errMsg);
        setAssembled(null);
        setLoading(false);
        return;
      }

      try {
        const fbSubParam = fallback.subType ? `&sub_type=${fallback.subType}` : '';
        const result = await apiPost<AssembledServiceResponse>(
          `/service/assemble?target_date=${date}&service_type=${fallback.serviceType}&temple_id=${templeId}&language=${lang}&calendar_style=${calendarStyle}&mode=${mode}${hierParam}${fbSubParam}`,
          {}
        );
        setAssembled(result);
        setFallbackUsed(fallback.serviceType);
      } catch (fbErr: any) {
        setError(fbErr.message || t('app.error'));
        setAssembled(null);
      }
    } finally {
      setLoading(false);
    }
  }, [date, serviceType, subType, templeId, locale, calendarStyle, mode, isHierarchical, t]);

  /* ─── Auto-assemble on date/service/hierarchical change ─── */
  const assembleRef = useRef(assemble);
  assembleRef.current = assemble;
  const [hasAssembled, setHasAssembled] = useState(false);

  useEffect(() => {
    if (!hasAssembled) return;
    const timer = setTimeout(() => assembleRef.current(), 300);
    return () => clearTimeout(timer);
  }, [date, serviceType, subType, isHierarchical, calendarStyle, hasAssembled]);

  /* ─── Auto-assemble on initial load (time-based service detection) ─── */
  useEffect(() => {
    if (!hasAssembled) {
      setHasAssembled(true);
      assemble();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ─── Scroll Observer ─── */
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(docHeight > 0 ? scrollY / docHeight : 0);
      setShowRunningHeader(scrollY > 400);
      setShowBackToTop(scrollY > 600);

      const sections = document.querySelectorAll('[data-nav-title]');
      for (let i = sections.length - 1; i >= 0; i--) {
        if ((sections[i] as HTMLElement).offsetTop - 120 <= scrollY) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  /* ─── Close dropdowns on outside click ─── */
  useEffect(() => {
    const handleClick = () => {
      setShowFontMenu(false);
      setShowServicePicker(false);
    };
    if (showFontMenu || showServicePicker) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [showFontMenu, showServicePicker]);

  /* ─── Derived: section list for TOC ─── */
  const slotLabels = t.raw('slot_labels') as Record<string, string>;
  const sections = assembled?.blocks?.map((block, i) => ({
    id: `section-${i}`,
    title: slotLabels[block.slot_key] || block.title || block.slot_key,
    type: block.block_type,
  })) || [];

  const dayInfo = litDay || assembled?.liturgical_day;

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const stopProp = (e: React.MouseEvent) => e.stopPropagation();

  const handleAssemble = () => {
    setHasAssembled(true);
    assemble();
  };

  return (
    <>
      {/* ─── Progress Bar ─── */}
      <div className="progress-bar" style={{ width: `${scrollProgress * 100}%` }} />

      {/* ═══════════════════════════════════════
          DESKTOP: Sidebar
          ═══════════════════════════════════════ */}
      <aside className="sidebar">
        {assembled && sections.length > 0 ? (
          <>
            <div className="sidebar-label">{t('service.service_order')}</div>
            {sections.map((sec, i) => (
              <a
                key={sec.id}
                className={`sidebar-item ${activeSection === sec.id ? 'active' : ''}`}
                onClick={() => scrollToSection(sec.id)}
              >
                <span className="sidebar-item-num">{i + 1}</span>
                <span className="sidebar-item-text">{sec.title}</span>
              </a>
            ))}
            <div className="sidebar-divider" />
            <div className="sidebar-stats">
              <span className="sidebar-stat">📄 {sections.length} {t('service.sections')}</span>
            </div>
          </>
        ) : (
          <div className="sidebar-empty">
            {t('service.assemble_prompt')}
          </div>
        )}
      </aside>

      {/* ═══════════════════════════════════════
          DESKTOP: Content Area
          ═══════════════════════════════════════ */}
      <div className="content-area">
        {/* Controls Bar */}
        <div className="controls-bar">
          {/* Service Picker */}
          <div className="service-picker" onClick={stopProp}>
            <button
              className={`service-picker-trigger ${showServicePicker ? 'open' : ''}`}
              onClick={() => setShowServicePicker(!showServicePicker)}
            >
              {(t.raw('service_types') as Record<string, string>)[serviceType] || serviceType}
              <span className="arrow">▾</span>
            </button>
            {showServicePicker && (
              <div className="service-picker-dropdown open">
                <div className="service-picker-group-label">{t('service.daily_cycle')}</div>
                {SERVICE_TYPES.map((st) => (
                  <div
                    key={st}
                    className={`service-picker-item ${st === serviceType ? 'active' : ''}`}
                    onClick={() => { setServiceType(st); setSubType(undefined); setFallbackUsed(null); setShowServicePicker(false); }}
                  >
                    {(t.raw('service_types') as Record<string, string>)[st] || st}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Date Picker */}
          <AdminDatePicker
            value={date}
            onChange={(d) => setDate(d ?? new Date().toISOString().split('T')[0])}
            placeholder={t('home.selectDate')}
            localeConfig={calLocale}
            showClear={false}
            style={{ width: 'auto' }}
          />

          {/* Calendar Pill Group */}
          <div className="pill-group">
            <button
              className={`pill ${calendarStyle === 'new' ? 'active' : ''}`}
              onClick={() => setCalendarStyle('new')}
              title={t('service.new_calendar_tooltip')}
            >
              {t('service.new_calendar')}
            </button>
            <button
              className={`pill ${calendarStyle === 'old' ? 'active' : ''}`}
              onClick={() => setCalendarStyle('old')}
              title={t('service.old_calendar_tooltip')}
            >
              {t('service.old_calendar')}
            </button>
          </div>

          {/* Mode Pill Group */}
          <div className="pill-group">
            <button
              className={`pill ${mode === 'full' ? 'active' : ''}`}
              onClick={() => setMode('full')}
            >
              {t('service.mode_full')}
            </button>
            <button
              className={`pill ${mode === 'ustav' ? 'active' : ''}`}
              onClick={() => setMode('ustav')}
            >
              {t('service.mode_ustav')}
            </button>
          </div>

          {/* Hierarchical Pill Group */}
          <div className="pill-group">
            <button
              className={`pill ${!isHierarchical ? 'active' : ''}`}
              onClick={() => setIsHierarchical(false)}
            >
              {t('service.hierarchical_off')}
            </button>
            <button
              className={`pill ${isHierarchical ? 'active' : ''}`}
              onClick={() => setIsHierarchical(true)}
            >
              {t('service.hierarchical_on')}
            </button>
          </div>

          {/* Assemble Button */}
          <button
            className="btn-assemble"
            onClick={handleAssemble}
            disabled={loading}
          >
            {loading ? '…' : t('service.assemble')}
          </button>
        </div>

        {/* Text Column */}
        <div className="text-column" ref={contentRef}>
          {/* Running Header */}
          <div className={`running-header ${showRunningHeader ? 'visible' : ''}`}>
            <span>{activeSection ? sections.find(s => s.id === activeSection)?.title : ''}</span>
            <span>{date}</span>
          </div>

          {/* Shared Content */}
          <ServiceContent
            assembled={assembled}
            dayInfo={dayInfo}
            error={error}
            serviceType={serviceType}
            fallbackUsed={fallbackUsed}
            t={t}
            locale={locale}
          />
        </div>
      </div>

      {/* ═══════════════════════════════════════
          MOBILE: Content + Bottom Bar + Sheets
          ═══════════════════════════════════════ */}
      <div className="mobile-content">
        {/* Mobile Text Column */}
        <div className="mobile-text-column">
          <ServiceContent
            assembled={assembled}
            dayInfo={dayInfo}
            error={error}
            serviceType={serviceType}
            fallbackUsed={fallbackUsed}
            t={t}
            locale={locale}
          />
        </div>
      </div>

      {/* Mobile Bottom Bar: Book | ✦ | List */}
      <div className="bottom-bar">
        <button className="bottom-action" onClick={() => setShowServiceSheet(true)} title={t('nav.service')}>
          <BookIcon size={22} />
        </button>
        <button
          className={`bottom-action-center ${showMobileSettings ? 'active' : ''}`}
          onClick={() => setShowMobileSettings(!showMobileSettings)}
          title="Réglages"
        >
          ✦
        </button>
        <button className="bottom-action" onClick={() => setShowTocSheet(true)} title={t('service.toc')}>
          <ListIcon size={22} />
        </button>
      </div>

      {/* Mobile TOC Sheet */}
      <div className={`bottom-sheet-overlay ${showTocSheet ? 'open' : ''}`} onClick={() => setShowTocSheet(false)} />
      <div className={`bottom-sheet ${showTocSheet ? 'open' : ''}`}>
        <div className="bottom-sheet-handle" />
        <div className="bottom-sheet-title">{t('service.toc')}</div>
        {sections.map((sec) => (
          <div
            key={sec.id}
            className={`sheet-item ${activeSection === sec.id ? 'active' : ''}`}
            onClick={() => { scrollToSection(sec.id); setShowTocSheet(false); }}
          >
            <span className="sheet-item-text">{sec.title}</span>
          </div>
        ))}
        {sections.length === 0 && (
          <div className="mobile-toc-empty">
            {t('service.assemble_prompt')}
          </div>
        )}
      </div>

      {/* Mobile Service Picker Sheet */}
      <div className={`bottom-sheet-overlay ${showServiceSheet ? 'open' : ''}`} onClick={() => setShowServiceSheet(false)} />
      <div className={`bottom-sheet ${showServiceSheet ? 'open' : ''}`}>
        <div className="bottom-sheet-handle" />
        <div className="bottom-sheet-title">{t('nav.service')}</div>
        {SERVICE_TYPES.map((st) => (
          <div
            key={st}
            className={`sheet-item ${st === serviceType ? 'active' : ''}`}
            onClick={() => { setServiceType(st); setSubType(undefined); setFallbackUsed(null); setShowServiceSheet(false); }}
          >
            <span className="sheet-item-text">{(t.raw('service_types') as Record<string, string>)[st] || st}</span>
          </div>
        ))}
      </div>

      {/* Mobile Settings Sheet */}
      <div className={`bottom-sheet-overlay ${showMobileSettings ? 'open' : ''}`} onClick={() => setShowMobileSettings(false)} />
      <div className={`bottom-sheet ${showMobileSettings ? 'open' : ''}`}>
        <div className="bottom-sheet-handle" />
        <div className="bottom-sheet-title">Réglages</div>

        {/* Calendar */}
        <div className="settings-group">
          <div className="settings-label">{t('service.calendar_style')}</div>
          <div className="pill-group">
            <button
              className={`pill ${calendarStyle === 'new' ? 'active' : ''}`}
              onClick={() => setCalendarStyle('new')}
              title={t('service.new_calendar_tooltip')}
            >
              {t('service.new_calendar')}
            </button>
            <button
              className={`pill ${calendarStyle === 'old' ? 'active' : ''}`}
              onClick={() => setCalendarStyle('old')}
              title={t('service.old_calendar_tooltip')}
            >
              {t('service.old_calendar')}
            </button>
          </div>
        </div>

        {/* Mode */}
        <div className="settings-group">
          <div className="settings-label">{t('service.mode_label')}</div>
          <div className="pill-group">
            <button
              className={`pill ${mode === 'full' ? 'active' : ''}`}
              onClick={() => setMode('full')}
            >
              {t('service.mode_full')}
            </button>
            <button
              className={`pill ${mode === 'ustav' ? 'active' : ''}`}
              onClick={() => setMode('ustav')}
            >
              {t('service.mode_ustav')}
            </button>
          </div>
        </div>

        {/* Hierarchical */}
        <div className="settings-group">
          <div className="settings-label">{t('service.hierarchical_label')}</div>
          <div className="pill-group">
            <button
              className={`pill ${!isHierarchical ? 'active' : ''}`}
              onClick={() => setIsHierarchical(false)}
            >
              {t('service.hierarchical_off')}
            </button>
            <button
              className={`pill ${isHierarchical ? 'active' : ''}`}
              onClick={() => setIsHierarchical(true)}
            >
              {t('service.hierarchical_on')}
            </button>
          </div>
        </div>

        {/* Date */}
        <div className="settings-group settings-column">
          <div className="settings-label">{t('service.select_date')}</div>
          <AdminDatePicker
            value={date}
            onChange={(d) => setDate(d ?? new Date().toISOString().split('T')[0])}
            placeholder={t('home.selectDate')}
            localeConfig={calLocale}
            showClear={false}
            style={{ width: '100%' }}
            mobile
          />
        </div>

        {/* Font Scale */}
        <div className="settings-group">
          <div className="settings-label">Aa</div>
          <div className="pill-group">
            {FONT_SCALES.map((s) => (
              <button
                key={s.value}
                className={`pill ${fontScale === s.value ? 'active' : ''}`}
                onClick={() => setFontScale(s.value)}
              >
                {s.labelKey === 'small' ? 'A' : s.labelKey === 'normal' ? 'Aa' : s.labelKey === 'large' ? 'Aa+' : s.labelKey === 'very_large' ? 'AA' : 'AAA'}
              </button>
            ))}
          </div>
        </div>

        {/* Assemble */}
        <button
          className="btn-assemble btn-assemble-full"
          onClick={handleAssemble}
          disabled={loading}
        >
          {loading ? '…' : t('service.assemble')}
        </button>
      </div>

      {/* Back to Top */}
      <button
        className={`back-to-top ${showBackToTop ? 'visible' : ''}`}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      >
        ↑
      </button>
    </>
  );
}
